<template>
  <v-card v-if="item" class="job-card mb-2 overflow-hidden" role="button" tabindex="0" @click="emit('click', item)"
    @keydown.enter="emit('click', item)" @keydown.space.prevent="emit('click', item)">
    <div class="status-strip" :style="{ backgroundColor: statusAccent(item) }"></div>

    <v-card-text class="py-3 ps-5 pe-4">
      <v-row align="start" class="ma-0">
        <v-col cols="12" md="11" class="pa-0">
          <div class="d-flex align-center flex-wrap ga-2">
            <h3 class="text-subtitle-1 font-weight-bold ma-0">{{ orderNumber(item) }}</h3>
            <JobTypeChip :typeId="item.JobTypeId" :typeName="item.JobTypeName" />
            <JobStatusChip :statusId="item.StatusId" :statusName="item.StatusName" />
          </div>

          <v-row class="my-2">
            <v-col cols="12" sm="6" lg="3">
              <div class="d-flex align-center ga-1 text-medium-emphasis text-body-2">
                <v-icon size="17">mdi-calendar-clock</v-icon>
                <span class="text-truncate">{{ appointmentText(item) }}</span>
              </div>
            </v-col>
            <v-col cols="12" sm="6" lg="3">
              <div class="d-flex align-center ga-1 text-medium-emphasis text-body-2">
                <v-icon size="17">mdi-flag-outline</v-icon>
                <span class="text-truncate">{{ priorityText(item) }}</span>
              </div>
            </v-col>
            <v-col cols="12" sm="6" lg="3">
              <div class="d-flex align-center ga-1 text-medium-emphasis text-body-2">
                <v-icon size="17">mdi-clock-outline</v-icon>
                <span class="text-truncate">{{ estimatedTimeText(item) }}</span>
              </div>
            </v-col>
            <v-col cols="12" sm="6" lg="3">
              <div class="d-flex align-center ga-1 text-medium-emphasis text-body-2">
                <v-icon size="17">mdi-calendar-plus</v-icon>
                <span class="text-truncate">{{ createdDateText(item) }}</span>
              </div>
            </v-col>
          </v-row>

          <div v-if="addressText(item)" class="d-flex align-center ga-1 text-body-2 text-medium-emphasis mt-1">
            <v-icon size="17">mdi-map-marker-outline</v-icon>
            <span class="text-truncate">{{ addressText(item) }}</span>
          </div>
        </v-col>

        <v-col cols="12" md="1" class="pa-0 d-none d-md-flex justify-end align-center">
          <v-icon size="22" color="medium-emphasis">mdi-chevron-right</v-icon>
        </v-col>
      </v-row>
    </v-card-text>

    <v-row no-gutters align="center" class="px-4 pb-2 text-body-2 text-medium-emphasis">
      <v-col cols="auto" class="mr-3">
        {{ childJobs(item).length }} {{ t("text.equipment").toLowerCase() }}
      </v-col>
      <v-col><v-divider /></v-col>
    </v-row>

    <v-card-text v-if="childJobs(item).length > 1" class="pt-1 pb-3 ps-5 pe-4">
      <v-row>
        <v-col v-for="machineJob in displayedChildJobs(item)" :key="machineJob.Id" cols="12"
          :md="childJobs(item).length === 1 ? 12 : 6" :lg="childJobs(item).length === 1 ? 12 : 4">
          <button type="button" class="machine-btn w-100 d-flex align-center ga-2 pa-2"
            @click.stop="emit('machineClick', machineJob)">
            <MachineModelImage :relativePath="machineImage(machineJob)" :size="34" />
            <span class="machine-text d-flex flex-column text-left">
              <span class="text-body-2 font-weight-bold text-truncate">{{ machineTitle(machineJob) }}</span>
              <span class="text-caption text-medium-emphasis text-truncate">{{ machineMeta(machineJob) }}</span>
              <span v-if="commentPreview(machineJob)" class="d-flex align-center ga-1 mt-1 comment-row">
                <v-icon size="14">mdi-comment-text-outline</v-icon>
                <span class="text-truncate comment-text">{{ commentPreview(machineJob) }}</span>
              </span>
            </span>
            <v-icon v-if="machineJob.Flag" size="15" color="#c97916">mdi-flag</v-icon>
          </button>
        </v-col>
        <v-col v-if="remainingChildCount(item)" cols="12" md="6" lg="4">
          <v-sheet rounded class="d-flex align-center justify-center px-2 py-3 bg-grey-lighten-4 font-weight-bold">
            +{{ remainingChildCount(item) }}
          </v-sheet>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-text v-else-if="firstChildJob(item)" class="pt-1 pb-3 ps-5 pe-4">
      <v-row no-gutters align="center">
        <v-col cols="auto" class="pr-3">
          <MachineModelImage :relativePath="machineImage(firstChildJob(item))" :size="34" />
        </v-col>
        <v-col class="d-flex flex-column">
          <span class="text-body-2 font-weight-bold text-truncate">{{ machineTitle(firstChildJob(item)) }}</span>
          <span class="text-caption text-medium-emphasis text-truncate">{{ machineMeta(firstChildJob(item)) }}</span>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col class="pr-3">
          <span v-if="commentPreview(firstChildJob(item))" class="d-flex align-center ga-1 mt-1 comment-row">
            <v-icon size="14">mdi-comment-text-outline</v-icon>
            <span class="text-truncate comment-text">{{ commentPreview(firstChildJob(item)) }}</span>
          </span>
        </v-col>
        <v-col>
          <span v-if="invoiceCommentPreview(firstChildJob(item))" class="d-flex align-center ga-1 mt-1 comment-row">
            <v-icon size="14">mdi-comment-text-outline</v-icon>
            <span class="text-truncate comment-text">{{ invoiceCommentPreview(firstChildJob(item)) }}</span>
          </span>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>


<script>
import MachineModelImage from "@/components/Cards/MachineModelImage.vue";
import { statuscolor } from "@/helpers/appGlobals";
import { lightTheme } from "@/plugins/vuetify/theme";
import JobStatusChip from '@/components/JobStatusChip.vue';
import JobTypeChip from '@/components/JobTypeChip.vue';

export default {
  name: "JobListItem",
  components: {
    MachineModelImage,
    JobStatusChip,
    JobTypeChip,
  },
  props: {
    item: {
      type: Object,
      default: null,
    },
    columnsPerRow: {
      type: Number,
      default: 1,
    },
  },
  emits: ["click", "machineClick"],

  methods: {
    statuscolor,
    emit(eventName, payload) {
      this.$emit(eventName, payload);
    },
    t(key) {
      return this.$t ? this.$t(key) : key;
    },

    orderNumber(job) {
      if (!job) return "";
      return job.JobTypeId != 3 ? job.OrderId : "S" + job.Id;
    },

    firstChildJob(job) {
      const children = this.childJobs(job);
      return children.length > 0 ? children[0] : null;
    },

    childJobs(job) {
      return Array.isArray(job?.InverseParent) ? job.InverseParent.filter(Boolean) : [];
    },
    displayedChildJobs(job) {
      const jobs = this.childJobs(job);
      const maxSlots = this.columnsPerRow * 2; // max 2 rows

      // When overflowing, reserve one slot for the "+x" indicator tile.
      if (jobs.length > maxSlots) {
        return jobs.slice(0, Math.max(maxSlots - 1, 0));
      }

      return jobs.slice(0, maxSlots);
    },
    remainingChildCount(job) {
      return Math.max(this.childJobs(job).length - this.displayedChildJobs(job).length, 0);
    },
    machineImage(machineJob) {
      return machineJob?.Machine?.ModelImage || undefined;
    },
    machineTitle(machineJob) {
      if (!machineJob) return "";
      return machineJob.Machine?.DisplayName || machineJob.DisplayName || machineJob.Name || machineJob.OrderId || "";
    },
    machineMeta(machineJob) {
      if (!machineJob) return "";

      const machine = machineJob.Machine || {};
      return [machine.SupplierName, machine.MachineType, machine.Facility].filter(Boolean).join(" - ") || machineJob.CustomerComment || machineJob.OrderId || "";
    },
    priorityText(job) {
      return job?.JobPriority?.Name || this.t("text.priority");
    },
    estimatedTimeText(job) {
      if (!job?.EstimatedTime) return "-";
      return job.EstimatedTime + " h";
    },
    createdDateText(job) {
      return this.formatDate(job?.CreatedDate);
    },
    appointmentText(job) {
      const appointments = Array.isArray(job?.Appointments) ? job.Appointments : [];
      if (!appointments.length) return this.t("text.not_scheduled");

      const appointment = appointments[0];
      const start = appointment.StartTime || appointment.AppointmentDate;
      const end = appointment.EndTime;
      if (!start) return this.t("text.scheduledfor");

      const startDate = new Date(start);
      let text = this.formatDate(start);

      if (appointment.StartTime && !Number.isNaN(startDate.getTime())) {
        text += " " + startDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      }

      if (end) {
        text += "-" + new Date(end).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      }

      if (appointments.length > 1) {
        text += " +" + (appointments.length - 1);
      }

      return text;
    },
    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "-";
      return date.toLocaleDateString();
    },
    addressText(job) {
      if (!job) return "";

      return [
        job.CustomerDeliveryAdressName,
        job.CustomerDeliveryAdressStreet,
        [job.CustomerDeliveryAdressZip, job.CustomerDeliveryAdressCity].filter(Boolean).join(" "),
        job.CustomerDeliveryAdressCountry,
      ].filter(Boolean).join(", ");
    },
    commentPreview(job) {
      if (!job?.CustomerComment) return "";
      return job.CustomerComment.replace(/\s+/g, " ").trim();
    },
    invoiceCommentPreview(job) {
      if (!job?.InvoiceComment) return "";
      return job.InvoiceComment.replace(/\s+/g, " ").trim();
    },

    statusAccent(job) {
      return lightTheme[statuscolor(job?.StatusId)] || lightTheme.disabled;
    },
  },

  watch: {},
};
</script>


<style lang="scss" scoped>
.job-card {
  cursor: pointer;
  position: relative;
  transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
}

.job-card:hover,
.job-card:focus-visible {
  border-color: rgba(30, 116, 92, 0.32);
  box-shadow: 0 12px 30px rgba(24, 39, 75, 0.11);
  outline: none;
  transform: translateY(-1px);
}

.status-strip {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 4px;
}

.machine-btn {
  background: #fbfcfd;
  border: 1px solid rgba(24, 39, 75, 0.09);
  border-radius: 7px;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.machine-btn:hover,
.machine-btn:focus-visible {
  background: #f2f7fb;
  border-color: rgba(30, 116, 92, 0.24);
  outline: none;
}

.machine-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
}

.comment-row {
  min-width: 0;
  overflow: hidden;
}

.comment-text {
  display: block;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>